// Shared card-property accessors, sort keys, and filter-label maps.
// Used by the deck editor (browse the player's collection) and the
// auction house (browse listings). Kept UI-agnostic — no DOM, no state
// — so both views can compose these into their own compare / filter
// pipelines without a shared parent view.

import type { CardDef, AbilityType } from '../types.js';
import type { Target } from '../shared/targeting.js';

/** Rarity → sort priority. Lower = higher-value (legendary sorts to
 *  the top by default). Used by both the deck editor and auction view
 *  when sorting by rarity. */
export const RARITY_ORDER: Record<string, number> = { legendary: 0, rare: 1, uncommon: 2, common: 3 };

/** Character cards have a power stat; action cards don't. This helper
 *  returns 0 for actions so power sorts don't blow up on mixed lists. */
export function getCardPower(def: CardDef): number {
  return def.type === 'character' ? def.power : 0;
}

export function getCardSpirit(def: CardDef): number {
  return def.spirit;
}

export function getCardClock(def: CardDef): number {
  return def.clock ?? 1;
}

/** The card's ability/effect slug (`damage`, `boost`, `passive-leech`,
 *  etc.). Null for characters with no ability. Action cards always
 *  carry an effect. */
export function getCardEffect(def: CardDef): string | null {
  if (def.type === 'character') return def.ability?.effect ?? null;
  return def.effect;
}

/** The card's trigger type (`on-play`, `passive`, `activated`, …).
 *  Characters only — actions don't fit the trigger taxonomy. */
export function getCardAbilityType(def: CardDef): AbilityType | null {
  if (def.type === 'character') return def.ability?.type ?? null;
  return null;
}

/** The card's target selector — for verb-based effects. Returns null
 *  for atomic-slug effects and characters without abilities. */
export function getCardTarget(def: CardDef): Target | null {
  if (def.type === 'character') return def.ability?.target ?? null;
  return def.target ?? null;
}

// Explicit lifecycle ordering for the trigger filter dropdown. The
// deck editor and auction browse both render this exact order so
// players learn the taxonomy in one place.
export const ABILITY_TYPE_ORDER: readonly AbilityType[] = [
  'on-play',
  'on-play-character',
  'on-play-action',
  'on-attack',
  'on-attack-alone',
  'on-damage-opponent',
  'beginning-of-turn',
  'end-of-turn',
  'passive',
  'activated',
];

export const ABILITY_TYPE_LABEL: Record<AbilityType, string> = {
  'on-play': 'On Play',
  'on-play-character': 'On Play Character',
  'on-play-action': 'On Play Action',
  'on-attack': 'On Attack',
  'on-attack-alone': 'On Attack Alone',
  'on-damage-opponent': 'On Damaging Opponent',
  'beginning-of-turn': 'Beginning of Turn',
  'end-of-turn': 'End of Turn',
  'passive': 'Passive',
  'activated': 'Activated',
};

// Targets grouped by side (ally → enemy → either → split) and
// narrow-to-broad within each group so the dropdown reads naturally.
export const TARGET_ORDER: readonly Target[] = [
  'self',
  'strongest-ally',
  'weakest-ally',
  'all-other-allies',
  'all-allies',
  'strongest-enemy',
  'weakest-enemy',
  'all-enemies',
  'strongest-character',
  'weakest-character',
  'all-characters',
  'self-and-strongest-enemy',
  'self-and-weakest-enemy',
];

export const TARGET_LABEL: Record<Target, string> = {
  'self': 'Self',
  'strongest-ally': 'Strongest Ally',
  'weakest-ally': 'Weakest Ally',
  'all-other-allies': 'All Other Allies',
  'all-allies': 'All Allies',
  'strongest-enemy': 'Strongest Enemy',
  'weakest-enemy': 'Weakest Enemy',
  'all-enemies': 'All Enemies',
  'strongest-character': 'Strongest Character',
  'weakest-character': 'Weakest Character',
  'all-characters': 'All Characters',
  'self-and-strongest-enemy': 'Self + Strongest Enemy',
  'self-and-weakest-enemy': 'Self + Weakest Enemy',
};

const EFFECT_LABEL_OVERRIDES: Record<string, string> = {
  'damage-opponent': 'Direct Damage',
  'draw-and-spirit': 'Draw + Spirit',
  'return-exchange': 'Return Exchange',
  'grant-extra-rally': 'Extra Rally',
  'grant-extra-support': 'Extra Support',
  'discard-enemy-hand': 'Discard Enemy Hand',
  'mill-enemy': 'Mill',
  'recycle-hand': 'Recycle',
  'shuffle-hand': 'Shuffle Hand',
};

/** Turn an effect slug into a human-readable filter label. Applies
 *  hand-written overrides first, then title-cases the fallback
 *  ("passive-leech" → "Leech (Passive)"). */
export function effectLabel(slug: string): string {
  if (EFFECT_LABEL_OVERRIDES[slug]) return EFFECT_LABEL_OVERRIDES[slug];
  const isPassive = slug.startsWith('passive-');
  const core = isPassive ? slug.slice('passive-'.length) : slug;
  const titled = core.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return isPassive ? `${titled} (Passive)` : titled;
}

export const RARITY_ORDER_LIST: readonly string[] = ['legendary', 'rare', 'uncommon', 'common'];

export const RARITY_LABEL: Record<string, string> = {
  legendary: 'Legendary',
  rare: 'Rare',
  uncommon: 'Uncommon',
  common: 'Common',
};
